import { useState, useMemo, useEffect, useCallback } from "react";
import { SortingState } from "@tanstack/react-table";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Breadcrumbs } from "ui/breadcrumbs";
import { useDisclosure } from "hooks/use-disclosure";

import BatchActionsMenu from "containers/admin/clients/client/approval-queue/batch-details/components/batch-actions-menu/batch-actions-menu";
import { Box, Typography } from "ui";
import { Button } from "ui/button";
import { Accordion } from "ui/accordion";
import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import { DataGrid, TableLoader, TableFillHeightBody, TableItemsNotFound } from "ui/data-grid";
import { BatchFormDialog } from "containers/admin/clients/client/approval-queue/batch-details/components/batch-form/batch-form-dialog";
import { AddTouchpointsDialog } from "containers/admin/clients/client/approval-queue/batch-details/components/add-touchpoints-dialog/add-touchpoints-dialog";
import VersionEditModal, {
  FormType,
} from "components/modals/version-edit-modal/version-edit-modal";
import { CloneTouchpointModal } from "components/modals/clone-touchpoint-modal/clone-touchpoint-modal";
import CampaignProvider from "containers/admin/clients/campaign/components/campaign-provider/campaign-provider";
import TouchpointProvider from "containers/admin/clients/touchpoint/components/touchpoint-provider/touchpoint-provider";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";

import {
  useGetTouchpointVersionStatusesQuery,
  useGetBatchStatusesQuery,
} from "hooks/api/dictionary";

import {
  fetchBatchDetails,
  fetchBatchTouchpointVersions,
  useAddTouchpointToBatch,
} from "hooks/api/use-batches";
import { useCampaigns } from "hooks/api/use-campaigns";
import { useTouchpoint } from "hooks/api/use-touchpoints";

import { createBatchTableColumns } from "containers/admin/clients/client/approval-queue/batch-details/components/touchpoints-table-columns/touchpoints-table-columns";
import useTouchpointActionMenuModals from "containers/admin/clients/client/approval-queue/batch-details/hooks/use-touchpoint-action-menu-items";
import useBatchActionMenuItems from "containers/admin/clients/client/approval-queue/batch-details/hooks/use-batch-action-menu-items";

import { Route as AppRoute, getClientPaths } from "utilities/app-routes";
import { touchpointVersionKeys } from "hooks/api/query-keys";
import { useCurrentClient } from "hooks/api/use-clients";
import { getBatchStatuses } from "containers/admin/clients/client/approval-queue/approval-queue.helpers";
import { formatDueDate, formatInputDate } from "utilities/date";

import {
  BatchItem,
  TouchpointItem,
} from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { TouchpointType } from "types/touchpoint";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

import {
  ACTIONS_SX,
  DESCRIPTION_SX,
  DUE_DATE_SX,
  HEADER_SX,
  PAGE_SX,
  STATUS_ROW_SX,
  TITLE_GROUP_SX,
  TITLE_SX,
} from "containers/admin/clients/client/approval-queue/batch-details/batch-details.styles";

import Icons from "assets";

import { truncateBatchName } from "./batch-details.utils";

const Batch = () => {
  const { id: batchId, clientId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentClient = useCurrentClient();
  const addTouchpointToBatchMutation = useAddTouchpointToBatch({ clientId });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TouchpointItem | undefined>(undefined);
  const [currentBatch, setCurrentBatch] = useState<BatchItem | null>(null);
  const [currentBatchTPVersions, setCurrentBatchTPVersions] = useState<TouchpointItem[]>([]);
  const [isApproveBatchModalOpen, setIsApproveBatchModalOpen] = useState(false);
  const [iEditBatchModalOpen, setIEditBatchModalOpen] = useState(false);
  const [isDeleteBatchModalOpen, setIsDeleteBatchModalOpen] = useState(false);
  const [isAddCreativeModalOpen, setIsAddCreativeModalOpen] = useState(false);

  const { data: selectedTouchpoint } = useTouchpoint(
    selectedVersion?.touchpointId ?? "",
    { enabled: !!selectedVersion?.touchpointId },
    { clientId: clientId ?? "", campaignId: selectedVersion?.campaignId ?? "" },
  );

  const editVersionModal = useDisclosure();
  const newDraftModal = useDisclosure();
  const cloneTouchpointModal = useDisclosure();

  // Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pagesAmount, setPagesAmount] = useState(0);
  const [{ pageSize = 25, totalRecords = 0 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  // Sorting state
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "touchpointName", desc: false },
  ]);

  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();
  const { data: batchStatuses = [] } = useGetBatchStatusesQuery();

  const { data: campaignsPage } = useCampaigns(
    { clientId: clientId ?? "", unpaged: true },
    { enabled: !!clientId && isAddCreativeModalOpen },
  );

  const campaignSelectOptions = useMemo(
    () =>
      (campaignsPage?.items ?? []).map((campaign) => ({
        label: campaign.name,
        value: campaign.id,
      })),
    [campaignsPage?.items],
  );

  const fetchBatch = useCallback(async () => {
    try {
      if (!clientId) {
        return;
      }

      const batch = await fetchBatchDetails({ clientId: clientId, batchId: batchId! });

      batch.dueDate = formatInputDate(batch.dueDate);

      batch && setCurrentBatch(batch);
    } catch (error) {
      setCurrentBatch(null);
    }
  }, [clientId, batchId]);

  const fetchTPVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!clientId) {
        return;
      }

      const batchTPVersions = await fetchBatchTouchpointVersions({
        clientId: clientId,
        batchId: batchId!,
        options: {
          size: pageSize,
          page: pageIndex,
          sort: sortingParams
            .map((param) => `${param.id},${param.desc ? "desc" : "asc"}`)
            .join(","),
        },
      });

      batchTPVersions && setCurrentBatchTPVersions(batchTPVersions.items);

      setPagesAmount(batchTPVersions.totalPages);
      setAmount({ pageSize, totalRecords: batchTPVersions.totalItems });
    } catch (error) {
      setCurrentBatchTPVersions([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, batchId, sortingParams, pageSize, pageIndex]);

  const { handleEditBatch, handleDeleteBatch, handleSendForReview, handleApproveBatch } =
    useBatchActionMenuItems({
      clientId,
      currentBatch,
      fetchBatch,
      setIEditBatchModalOpen,
    });

  const {
    handleCreateDraftFromVersion,
    handleSubmitNewDraft,
    handleSubmitEditVersion,
    handleDeleteTouchpointFromBatch,
  } = useTouchpointActionMenuModals({
    selectedVersion,
    setSelectedVersion,
    editVersionModal,
    newDraftModal,
    currentBatch,
    clientId: clientId!,
    fetchBatch,
  });

  const getTouchpointVersionById = (id: string) => {
    return currentBatchTPVersions.find((v: TouchpointItem) => v.id === id);
  };

  // Create table columns
  const columns = useMemo(
    () =>
      createBatchTableColumns({
        clientId: clientId!,
        batchId: batchId!,
        batchName: currentBatch?.name ?? "…",
        touchpointVersionStatuses,
        hasTemplates: currentBatchTPVersions.some(
          (tp) => tp.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME,
        ),
        onEdit: (id: string) => {
          const version = getTouchpointVersionById(id);

          setSelectedVersion(version);

          editVersionModal.onOpen();
        },
        onOpenInEditor: (id: string) => {
          const version = getTouchpointVersionById(id);

          setSelectedVersion(version);

          const link = `${AppRoute.clients}/${clientId}/${AppRoute.campaigns}/${version?.campaignId}/${AppRoute.creative}/${version?.touchpointId}/${AppRoute.versions}/${version?.id}/${AppRoute.emailBuilder}/${AppRoute?.design}`;

          navigate(link);
        },
        onCreateDraft: (id: string) => {
          const version = getTouchpointVersionById(id);

          setSelectedVersion(version);

          handleCreateDraftFromVersion();
        },
        onClone: (id: string) => {
          const version = getTouchpointVersionById(id);
          setSelectedVersion(version);

          cloneTouchpointModal.onOpen();
        },
        onRemoveFromQueue: async (id: string) => {
          const version = getTouchpointVersionById(id);
          setSelectedVersion(version);

          await handleDeleteTouchpointFromBatch(id);
        },
      }),
    [
      clientId,
      touchpointVersionStatuses,
      currentBatchTPVersions,
      getTouchpointVersionById,
      editVersionModal,
      navigate,
      handleCreateDraftFromVersion,
      cloneTouchpointModal,
      handleDeleteTouchpointFromBatch,
    ],
  );

  useEffect(() => {
    clientId && batchId && fetchBatch();
  }, [selectedVersion?.status, clientId, batchId, fetchBatch]);

  useEffect(() => {
    currentBatch && fetchTPVersions();
  }, [currentBatch, fetchTPVersions]);

  const handleAddTouchpointsToBatch = async ({
    chosenTouchpoints,
  }: {
    chosenTouchpoints: { value: string }[];
  }) => {
    const batchName = currentBatch?.name;

    try {
      await Promise.all(
        chosenTouchpoints.map(async (touchpoint) =>
          addTouchpointToBatchMutation.mutateAsync({
            batchId: batchId!,
            attributes: {
              touchpointVersionId: touchpoint.value,
            },
          }),
        ),
      );

      toast.success({
        data: {
          title: `Added to '${batchName}'`,
        },
      });

      queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
      fetchBatch();
    } catch (error) {
      console.error("Error adding touchpoints to batch:", error);
    } finally {
      setIsAddCreativeModalOpen(false);
    }
  };

  const { clientCampaignsPath, approvalQueuePath } = getClientPaths({ clientId: clientId! });

  return (
    currentBatch?.name && (
      <Box sx={PAGE_SX}>
        <Breadcrumbs
          sx={{ mt: 1.5 }}
          breadcrumbs={[
            { label: currentClient.name, href: clientCampaignsPath },
            { label: "Approval queue", href: approvalQueuePath },
            { label: truncateBatchName(currentBatch.name), active: true },
          ]}
        />
        <Box sx={HEADER_SX}>
          <Box sx={TITLE_GROUP_SX}>
            <Typography variant="inherit" component="h1" sx={TITLE_SX}>
              {currentBatch?.name}
            </Typography>
            {currentBatch?.mondayBoardLink && (
              <Button
                href={currentBatch.mondayBoardLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="medium">
                Monday board
              </Button>
            )}
          </Box>
          <Box sx={ACTIONS_SX}>
            {(currentBatch?.status === "BUILDING" || currentBatch?.status === "IN_REVIEW") && (
              <Button
                onClick={() => setIsAddCreativeModalOpen(true)}
                variant="outlined"
                size="large"
                sx={{ minWidth: 110 }}>
                Add creative
              </Button>
            )}
            {!!currentBatchTPVersions?.length && currentBatch?.status === "BUILDING" && (
              <Button onClick={handleSendForReview} aria-label="Send for review" size="large">
                Send for review
              </Button>
            )}
            <BatchActionsMenu
              item={{ itemName: "Batch details", itemId: currentBatch?.id }}
              batchCanBeApproved={
                currentBatch?.status === "IN_REVIEW" && !!currentBatchTPVersions?.length
              }
              onApprove={() => setIsApproveBatchModalOpen(true)}
              onEdit={() => {
                setIEditBatchModalOpen(true);
              }}
              onDelete={() => setIsDeleteBatchModalOpen(true)}
            />
          </Box>
        </Box>
        <Box sx={STATUS_ROW_SX}>
          <Box sx={DUE_DATE_SX}>
            <Icons.Event width={20} height={20} />
            <Typography variant="inherit" component="span">
              {formatDueDate(currentBatch?.dueDate)}
            </Typography>
          </Box>
          {currentBatch?.status && (
            <StatusIndicator
              versionStatuses={getBatchStatuses(batchStatuses, currentBatch.status) ?? []}
            />
          )}
        </Box>

        <Accordion variant="secondary" title="Batch description" sx={{ mb: 2 }}>
          <Typography sx={DESCRIPTION_SX}>{currentBatch?.description}</Typography>
        </Accordion>
        <TableFillHeightBody>
          <TableLoader isLoading={isLoading} fullScreen />

          {!isLoading &&
            (currentBatchTPVersions?.length ? (
              <DataGrid
                columns={columns}
                data={currentBatchTPVersions || []}
                isLoading={false}
                showingRecordsIsOn={true}
                viewByAmountSelectIsOn={true}
                sortingParams={sortingParams}
                setSortingParams={setSortingParams}
                manualSorting={true}
                totalRecords={totalRecords}
                pageSize={pageSize}
                pagesAmount={pagesAmount}
                setAmount={setAmount}
                pageIndex={pageIndex}
                setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
                fillHeight
              />
            ) : (
              <TableItemsNotFound />
            ))}
        </TableFillHeightBody>

        {/* MODALS */}
        {/* Batch modals */}
        {/* Approve batch modal */}
        <ConfirmationDialog
          open={isApproveBatchModalOpen}
          onClose={() => setIsApproveBatchModalOpen(false)}
          intent="warning"
          headline="Are you sure you want to approve batch as client?"
          confirmButtonText="Approve as client"
          onConfirm={() => {
            handleApproveBatch();
            setIsApproveBatchModalOpen(false);
          }}
        />
        {/* Edit batch modal */}
        {iEditBatchModalOpen && (
          <BatchFormDialog
            headline="Edit batch"
            onClose={() => setIEditBatchModalOpen(false)}
            onSubmit={handleEditBatch}
            defaultValues={{
              name: currentBatch?.name,
              description: currentBatch?.description,
              dueDate: currentBatch?.dueDate,
              mondayBoardLink: currentBatch?.mondayBoardLink,
            }}
          />
        )}
        {/* Delete batch modal */}
        {isDeleteBatchModalOpen && (
          <ConfirmationDialog
            open={isDeleteBatchModalOpen}
            onClose={() => setIsDeleteBatchModalOpen(false)}
            intent="warning"
            headline="Are you sure you want to delete this batch?"
            confirmButtonText="Delete batch"
            onConfirm={() => {
              handleDeleteBatch();
              setIsDeleteBatchModalOpen(false);
            }}
          />
        )}

        {/* Touchpoint modals  */}
        {/* Add creative modal */}
        {isAddCreativeModalOpen && (
          <AddTouchpointsDialog
            clientId={clientId!}
            batchId={batchId!}
            campaignOptions={campaignSelectOptions}
            onClose={() => setIsAddCreativeModalOpen(false)}
            onSubmit={handleAddTouchpointsToBatch}
          />
        )}
        {selectedVersion?.id && (
          <CampaignProvider currentCampaignId={selectedVersion.campaignId}>
            {/*  Edit touchpoint modal */}
            <VersionEditModal
              {...editVersionModal}
              id={selectedVersion.id}
              isEmailType={selectedVersion.type === TouchpointType.EMAIL}
              placeholderNotes={selectedVersion.description}
              parentName={selectedVersion.campaignName}
              formType={FormType.EDIT}
              applyClientTheme={selectedVersion.applyClientTheme}
              name={selectedVersion.touchpointName}
              onSubmit={handleSubmitEditVersion}
              saveText="Save"
              touchpointId={selectedVersion.touchpointId}
              copyCode={selectedTouchpoint?.copyCode}
              isTemplate={selectedVersion.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME}
            />
            {/* Create new draft modal */}
            <VersionEditModal
              {...newDraftModal}
              id={selectedVersion?.id}
              isEmailType={selectedVersion.type === TouchpointType.EMAIL}
              parentName={selectedVersion.campaignName}
              formType={FormType.NEW_VERSION}
              name={selectedVersion.touchpointName}
              onSubmit={handleSubmitNewDraft}
              touchpointId={selectedVersion.touchpointId}
              copyCode={selectedTouchpoint?.copyCode}
              isTemplate={selectedVersion.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME}
            />
            {/* Clone touchpoint modal */}
            <TouchpointProvider currentTouchpointId={selectedVersion?.touchpointId}>
              <CloneTouchpointModal
                {...cloneTouchpointModal}
                selectedVersion={{
                  ...selectedVersion,
                  name: selectedVersion.touchpointName,
                }}
              />
            </TouchpointProvider>
          </CampaignProvider>
        )}
      </Box>
    )
  );
};

export default Batch;
